package com.milta.feature.route.presentation

import com.milta.feature.delivery.domain.entity.AddressSuggestion
import com.milta.feature.route.domain.entity.LoadingAddress

sealed interface WebImportRouteIntent {
    /** A file was picked (drag-drop, "Parcourir les fichiers"/"parcourez vos fichiers") — starts an
     * import from scratch, or adds its deliveries to the list already there (retour 26/10, R9). */
    data class PickFile(val fileBytes: ByteArray, val fileName: String) : WebImportRouteIntent

    /** Écran 1d — "Réessayer", replays the same file (transient 503/network error, no new pick needed). */
    data object RetryServiceCall : WebImportRouteIntent

    /** Écran 2 — a driver's checkbox was toggled (MIL-51 — multi-driver assignment, replaces the
     * former single-selection [SelectDriver]/dropdown). Adds [driverId] to
     * [WebImportRouteState.selectedDriverIds] if absent, removes it otherwise. */
    data class ToggleDriver(val driverId: String) : WebImportRouteIntent

    /** Écran 2 — pencil icon, a row was corrected in place. */
    data class UpdateRow(val rowId: Int, val row: WebImportRouteRow) : WebImportRouteIntent

    /** Écran 2 — "Optimiser l'itinéraire" tapped: creates the batch (`POST /route-batches`, MIL-39
     * contract, MIL-51) for every selected driver — always through the batch endpoint, even when a
     * single driver is selected (one code path, no N=1 special case). On success moves to Écran 3
     * (répartition) rather than dispatching immediately. */
    data object Submit : WebImportRouteIntent

    /** Écran 2d — "Envoyer sans optimisation" tapped: only offered when [Submit] just failed with
     * `optimization_unavailable` (RoutaR down). Reuses the same `POST /route-batches` batch endpoint
     * with `optimize = false` — the existing round-robin split already used for MIL-39 — so a manager
     * isn't stuck waiting on RoutaR to send tournées today. Not a new fallback engine, just exposing
     * an existing backend capability. */
    data object SubmitWithoutOptimization : WebImportRouteIntent

    /** Écran 3 — "Valider et envoyer les tournées" tapped: dispatches the whole batch (`POST
     * /route-batches/{id}/dispatch`, MIL-51). No re-optimization intent from this step — out of scope. */
    data object ValidateAndSend : WebImportRouteIntent

    data object ClearSubmitError : WebImportRouteIntent

    /** Écran 2 — "Annuler l'import" (bouton clair, confirmation côté écran avant d'émettre
     * l'intent) : efface le brouillon persistant ([WebImportRouteViewModel]'s localStorage key) et
     * revient à l'Écran 1 vierge — le scan OCR/IA déjà payé n'était pas récupérable autrement que de
     * garder ce brouillon, mais l'utilisateur doit pouvoir l'abandonner explicitement. */
    data object DiscardDraft : WebImportRouteIntent

    // ── MIL-99 — Adresse de chargement (Écran 2) ──
    /** A row of the loading-address list was clicked. */
    data class SelectLoadingAddress(val id: String) : WebImportRouteIntent
    /** A HERE suggestion was picked in the "Nouvelle adresse" field — POST /loading-addresses,
     * then it becomes the selection. */
    data class AddLoadingAddress(val suggestion: AddressSuggestion) : WebImportRouteIntent
    /** Per-row delete clicked — opens the confirmation dialog, nothing deleted yet. */
    data class RequestDeleteLoadingAddress(val address: LoadingAddress) : WebImportRouteIntent
    data object CancelDeleteLoadingAddress : WebImportRouteIntent
    /** Dialog "Supprimer" clicked — DELETE /loading-addresses/{id}. */
    data object ConfirmDeleteLoadingAddress : WebImportRouteIntent

    // ── MIL-99 — Écran 3, "Corriger l'adresse" on an unlocated stop of the (not yet sent) batch ──
    /** A HERE suggestion was picked — PUT /routes/{routeId}/deliveries/{deliveryId}/address. */
    data class FixBatchStopAddress(val routeId: String, val deliveryId: String, val suggestion: AddressSuggestion) : WebImportRouteIntent

    /** MIL-97 — Écran 3 (E2), "Ré-optimiser" the batch not sent yet: POST /route-batches/{id}/reoptimize. */
    data object ReoptimizeBatch : WebImportRouteIntent

    // ── Retour 26/10 (demande 2) ──
    /** Stepper "1 Importer" while a list exists: the drop zone, a file dropped there is added (R6/R9). */
    data object ShowImportStep : WebImportRouteIntent
    /** Stepper "2 Vérifier & assigner" from step 1 or 3: the table again, nothing lost (R6). */
    data object ShowVerifyStep : WebImportRouteIntent
    data class SetFilter(val filter: DeliveryFilter) : WebImportRouteIntent
    /** Pencil of a row ([rowId]) or "Saisie manuelle" (null). */
    data class OpenRowEditor(val rowId: Int?) : WebImportRouteIntent
    data object CloseRowEditor : WebImportRouteIntent
    /** "Enregistrer" of the modal: the address is checked by HERE first (R4). */
    data class SaveRowEditor(val row: WebImportRouteRow) : WebImportRouteIntent
    /** Per-row delete (R2: the other way out of an address to correct). */
    data class RemoveRow(val rowId: Int) : WebImportRouteIntent
    data object DismissAddedFileFailure : WebImportRouteIntent
}
